import React from 'react';

export interface BulkActionBarProps {
  selectedIds: string[];
  onClear: () => void;
  onPublish?: () => void;
  onDraft?: () => void;
  onSchedule?: () => void;
  onRetry?: () => void;
  onDelete?: () => void;
  publishCount?: number;
  draftCount?: number;
  scheduleCount?: number;
  retryCount?: number;
  isBusy?: boolean;
}

const ACTION_BUTTON_STYLE: React.CSSProperties = {
  padding: '5px 11px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 550,
  background: 'rgba(255,255,255,0.10)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.18)',
  cursor: 'pointer',
};

const PRIMARY_BUTTON_STYLE: React.CSSProperties = {
  ...ACTION_BUTTON_STYLE,
  background: 'var(--color-coral)',
  borderColor: 'var(--color-coral)',
  fontWeight: 600,
};

const CLEAR_BUTTON_STYLE: React.CSSProperties = {
  padding: '5px 9px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 550,
  background: 'transparent',
  color: 'rgba(255,255,255,0.7)',
  border: '1px solid transparent',
  cursor: 'pointer',
};

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedIds,
  onClear,
  onPublish,
  onDraft,
  onSchedule,
  onRetry,
  onDelete,
  publishCount,
  draftCount,
  scheduleCount,
  retryCount,
  isBusy = false,
}) => {
  if (selectedIds.length === 0) return null;

  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 8px 8px 14px',
        background: 'var(--color-ink)',
        color: '#fff',
        borderRadius: 10,
        boxShadow: '0 12px 32px -8px rgba(0,0,0,0.35)',
        zIndex: 30,
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 550 }}>
        {selectedIds.length} selected
      </span>
      <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.18)' }} />
      {onPublish && (
        <button type="button" style={PRIMARY_BUTTON_STYLE} onClick={onPublish} disabled={isBusy || publishCount === 0}>
          Publish{publishCount !== undefined ? ` (${publishCount})` : ''}
        </button>
      )}
      {onDraft && (
        <button type="button" style={ACTION_BUTTON_STYLE} onClick={onDraft} disabled={isBusy || draftCount === 0}>
          Draft{draftCount !== undefined ? ` (${draftCount})` : ''}
        </button>
      )}
      {onSchedule && (
        <button type="button" style={ACTION_BUTTON_STYLE} onClick={onSchedule} disabled={isBusy || scheduleCount === 0}>
          Schedule{scheduleCount !== undefined ? ` (${scheduleCount})` : ''}
        </button>
      )}
      {onRetry && (
        <button type="button" style={ACTION_BUTTON_STYLE} onClick={onRetry} disabled={isBusy || retryCount === 0}>
          Retry{retryCount !== undefined ? ` (${retryCount})` : ''}
        </button>
      )}
      {onDelete && (
        <button type="button" style={ACTION_BUTTON_STYLE} onClick={onDelete} disabled={isBusy}>
          Delete
        </button>
      )}
      <button type="button" style={CLEAR_BUTTON_STYLE} onClick={onClear}>
        Cancel
      </button>
    </div>
  );
};

export default BulkActionBar;
